import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, CheckCircle, AlertCircle, X } from "lucide-react";
import { usePdfExtract } from "@/hooks/use-pdf-extract";
import { PDFUploadModalProps, ExtractedData } from "@/types";
import { translateDomainName, translateStrengthName } from "@/lib/strength-i18n";

export const PDFUploadModal = ({
  open,
  onOpenChange,
  onDataExtracted,
}: PDFUploadModalProps) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { extractPdf, isExtracting } = usePdfExtract();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setError(null);
    } else {
      setError(t('pdf.invalidFileType'));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setError(null);
    } else {
      setError(t('pdf.invalidFileType'));
    }
  };

  const handleProcess = async () => {
    if (!file) return;

    setProgress(10);
    setError(null);

    try {
      const result = await extractPdf(file);
      
      if (result.success && result.data && result.data.extractedData) {
        const apiExtractedData = result.data.extractedData;
        const extracted: ExtractedData = {
          userName: apiExtractedData.username || result.data.username,
          strengths: apiExtractedData.strengths.map(s => ({
            rank: s.rank,
            name: s.strengthName
          })),
          topDomains: apiExtractedData.domains.map(d => d.domainName)
        };
        
        setExtractedData(extracted);
        setProgress(100);
      } else {
        setError(result.error || t('pdf.extractionError'));
      }
    } catch (err) {
      setError(t('pdf.extractionError'));
    }
  };

  const handleSave = async () => {
    if (!extractedData) return;

    // Extraction already stored the PDF and saved ranks on the backend.
    onDataExtracted?.(extractedData);
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setProgress(0);
    setExtractedData(null);
    setError(null);
    onOpenChange(false);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setExtractedData(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        dir={isRtl ? "rtl" : "ltr"}
      >
        <DialogHeader>
          <DialogTitle>{t('pdf.uploadTitle')}</DialogTitle>
          <DialogDescription>
            {t('pdf.uploadDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* File Upload Area */}
          {!extractedData && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors sm:p-8 ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              }`}
            >
              {!file ? (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">
                    {t('pdf.dragDrop')}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('pdf.dragDropDesc')}
                  </p>
                  <Button onClick={() => fileInputRef.current?.click()}>
                    {t('pdf.selectFile')}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </>
              ) : (
                <div className="flex flex-col gap-3 p-4 bg-muted rounded-lg sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText className="w-8 h-8 text-primary" />
                    <div className="min-w-0 text-left">
                      <p className="break-all font-medium">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRemoveFile}
                    disabled={isExtracting}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <Card className="p-4 border-destructive bg-destructive/5">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm">{error}</p>
              </div>
            </Card>
          )}

          {/* Processing Progress */}
          {isExtracting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('pdf.processing')}
                </span>
                <span className="font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Extracted Data Preview */}
          {extractedData && (
            <Card className="p-6 space-y-4 bg-accent/5 border-accent/20">
              <div className="flex items-center gap-2 text-accent">
                <CheckCircle className="w-5 h-5" />
                <p className="font-semibold">{t('pdf.extractionComplete')}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">{t('pdf.detectedName')}:</p>
                  <p className="font-medium">{extractedData.userName}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('pdf.topStrengths')}{" "}
                    {isRtl
                      ? `(${t("pdf.allStrengthsExtracted", { count: extractedData.strengths.length })})`
                      : `(${extractedData.strengths.length} total):`}
                  </p>
                  <div className="max-h-[300px] overflow-y-auto border border-border/50 rounded-lg p-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {extractedData.strengths.map((strength, index) => (
                        <div
                          key={`${strength.rank}-${index}`}
                          className="flex items-center gap-2 p-2 bg-background rounded"
                        >
                          <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                            #{strength.rank}
                          </Badge>
                          <span className="text-sm">
                            {translateStrengthName(strength.name, t, i18n.language)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('pdf.topDomains')}:
                  </p>
                  <div className="flex gap-2">
                    {extractedData.topDomains.map((domain) => (
                      <Badge key={domain} className="bg-primary/10 text-primary">
                        {translateDomainName(domain, t, i18n.language)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            {!extractedData && file && (
              <Button
                onClick={handleProcess}
                disabled={isExtracting}
                className="gap-2"
              >
                {isExtracting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    {t('pdf.processing')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {t('pdf.processFile')}
                  </>
                )}
              </Button>
            )}
            {extractedData && (
              <Button onClick={handleSave} className="gap-2">
                <CheckCircle className="w-4 h-4" />
                {t('pdf.saveData')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
